import React, { useEffect } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../../../../components/Layout';
import Reveal, { SplitWords } from '../../../../../components/Reveal';
import content, { CONTACT_EMAIL } from './content';

const LANGS = [
    { code: 'en', label: 'EN' },
    { code: 'zh', label: '中文' }
];

/**
 * One page, two editions. The language lives in the URL (/lianwan/privacy/en
 * and /lianwan/privacy/zh) so either edition can be linked to directly; an
 * unknown or missing segment falls back to English, which keeps the address
 * registered in App Store Connect (/lianwan/privacy) working unchanged.
 * Same shape as the EyesDwell policy page.
 * @author Nelson
 */
const PrivacyPolicyLianWan = () => {
    const navigate = useNavigate();
    const { lang } = useParams();
    const code = content[lang] ? lang : 'en';
    const t = content[code];

    useEffect(() => {
        const root = document.documentElement;
        const previous = root.lang;

        root.lang = t.htmlLang;

        return () => { root.lang = previous; };
    }, [t.htmlLang]);

    /* Blocks carry only their kind and payload, so the entrance stagger and the
       markup are decided here once rather than per language. */
    const renderBlock = ([kind, value], index) => {
        const delay = index === 0 ? 620 : 60;
        const key = `${code}-${index}`;

        if (kind === 'ul') {
            return (
                <Reveal as="ul" key={key} delay={delay}>
                    {value.map((item, itemIndex) => (
                        <li key={`${key}-${itemIndex}`}>{item}</li>
                    ))}
                </Reveal>
            );
        }

        return (
            <Reveal as={kind} key={key} delay={delay}>
                {value}
            </Reveal>
        );
    };

    return (
        <Layout title={t.docTitle} pad="tight">
            <header className="page__head">
                <div className="policy__bar">
                    <span className="reveal reveal--inline" style={{ '--reveal-delay': '60ms' }}>
                        <button type="button" className="btn" onClick={() => navigate(-1)}>
                            <span className="btn__arrow" aria-hidden="true">←</span> {t.back}
                        </button>
                    </span>

                    <span className="reveal reveal--inline" style={{ '--reveal-delay': '140ms' }}>
                        <nav className="langswitch" aria-label="Policy language">
                            {LANGS.map((item) => (
                                <NavLink
                                    key={item.code}
                                    to={`/lianwan/privacy/${item.code}`}
                                    replace
                                    lang={content[item.code].htmlLang}
                                    aria-current={item.code === code ? 'true' : undefined}
                                    className={
                                        item.code === code
                                            ? 'langswitch__opt langswitch__opt--on'
                                            : 'langswitch__opt'
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                        </nav>
                    </span>
                </div>

                <h2
                    className="page__title page__title--display"
                    style={{ marginTop: 'var(--sp-8)' }}
                    lang={t.htmlLang}
                >
                    <SplitWords text={t.title} start={220} />
                </h2>

                <Reveal className="policy__dates" delay={560} lang={t.htmlLang}>
                    <span className="policy__date">
                        <span className="u-mono">{t.effectiveLabel}</span>
                        <b>{t.effective}</b>
                    </span>
                    <span className="policy__date">
                        <span className="u-mono">{t.updatedLabel}</span>
                        <b>{t.updated}</b>
                    </span>
                </Reveal>
            </header>

            <div className="prose" lang={t.htmlLang}>
                {t.blocks.map(renderBlock)}
            </div>

            <Reveal className="policy__sign" delay={80} lang={t.htmlLang}>
                <p className="u-mono">{t.developerLabel}</p>
                <p className="policy__sign-name">YUAN LAB ENTERPRISE</p>
                <a className="u-link policy__sign-mail" href={`mailto:${CONTACT_EMAIL}`}>
                    {CONTACT_EMAIL}
                </a>
            </Reveal>
        </Layout>
    );
};

export default PrivacyPolicyLianWan;
